import { useCallback, useState } from "react";
import _ from "lodash";

import theme from "theme";

import PageHeader from "components/PageHeader";
import Stack from "components/Stack";
import toast from "components/Toast";
import { usePageTitle } from "components/usePageTitle";

import NotFound from "pages/404";

import { useSignedUser } from "entities/Auth/sdk";
import {
  ISuperuserDeal,
  IValueCreator,
  superuserCustomFieldValuesFromChecklistFileImportCreate,
} from "entities/Superuser/sdk";
import { useChecklistFileImportReady } from "entities/TableStorage/hooks";

import { EChecklistFileImportStep } from "./constants";
import DealSelect from "./DealSelect";
import Details from "./Details";
import FileUpload from "./FileUpload";
import ImportCompleted from "./ImportCompleted";
import UserSelect from "./UserSelect";

const SuperuserChecklistFileImport = () => {
  const [selectedDealId, setSelectedDealId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [checklistFileImportId, setChecklistFileImportId] = useState<
    number | null
  >(null);

  const [enableGuessedConnectionFetching, setEnableGuessedConnectionFetching] =
    useState<boolean>(false);

  const [currentStep, setCurrentStep] = useState<EChecklistFileImportStep>(
    EChecklistFileImportStep.DEAL_SELECT
  );
  const { data: user } = useSignedUser();

  usePageTitle("Builders Patch: Superuser Checklist File Import");

  const handleDealSelect = useCallback(({ deal }: { deal: ISuperuserDeal }) => {
    setSelectedDealId(deal.id);
    setCurrentStep(EChecklistFileImportStep.USER_SELECT);
  }, []);

  const handleUserSelectBack = useCallback(() => {
    setSelectedDealId(null);
    setCurrentStep(EChecklistFileImportStep.DEAL_SELECT);
  }, []);

  const handleUserSelect = useCallback(({ user }: { user: IValueCreator }) => {
    setSelectedUserId(user.id);
    setCurrentStep(EChecklistFileImportStep.FILE_UPLOAD);
  }, []);

  const handleFilesUploadBack = useCallback(() => {
    setSelectedUserId(null);
    setCurrentStep(EChecklistFileImportStep.USER_SELECT);
  }, []);

  const handleFilesUpload = useCallback(
    ({ checklistFileImportId }: { checklistFileImportId: number }) => {
      setChecklistFileImportId(checklistFileImportId);
      setCurrentStep(EChecklistFileImportStep.DETAILS);
    },
    []
  );

  const guessedConnectionFetchingSetter = useCallback(
    ({ enableFetching }: { enableFetching: boolean }) => {
      setEnableGuessedConnectionFetching(enableFetching);
    },
    [setEnableGuessedConnectionFetching]
  );

  useChecklistFileImportReady({
    successEventHandler: () =>
      guessedConnectionFetchingSetter({ enableFetching: true }),
  });

  const handleGuessedConnectionsSubmit = useCallback(() => {
    if (!_.isNil(checklistFileImportId) && !_.isNil(selectedDealId)) {
      setShowLoading(true);
      superuserCustomFieldValuesFromChecklistFileImportCreate({
        checklistFileImportId: checklistFileImportId,
        dealId: selectedDealId,
      })
        .then(() => {
          setCurrentStep(EChecklistFileImportStep.IMPORT_COMPLETED);
        })
        .catch((errors) => {
          const errorMessage = errors?.message || errors?.detail;
          if (!_.isNil(errorMessage)) {
            toast.errorMessage(errorMessage);
          } else {
            errors?.forEach?.((error: unknown) => {
              if (_.isString(error)) {
                toast.errorMessage(error);
              }
            });
          }
        })
        .finally(() => {
          setShowLoading(false);
          setEnableGuessedConnectionFetching(false);
        });
    }
  }, [checklistFileImportId, selectedDealId]);

  const handleStartProcessOver = useCallback(() => {
    setSelectedDealId(null);
    setSelectedUserId(null);
    setChecklistFileImportId(null);
    setCurrentStep(EChecklistFileImportStep.DEAL_SELECT);
  }, []);

  if (!user?.is_superuser) {
    return <NotFound />;
  }

  return (
    <Stack
      sx={{
        flexGrow: 1,
        padding: theme.spacing(0, 1),
      }}
    >
      <PageHeader title="Checklist File Import" />

      {currentStep === EChecklistFileImportStep.DEAL_SELECT && (
        <DealSelect onStepAdvance={handleDealSelect} />
      )}

      {currentStep === EChecklistFileImportStep.USER_SELECT &&
        !_.isNil(selectedDealId) && (
          <UserSelect
            onStepBack={handleUserSelectBack}
            onStepAdvance={handleUserSelect}
            selectedDealId={selectedDealId}
          />
        )}

      {currentStep === EChecklistFileImportStep.FILE_UPLOAD &&
        !_.isNil(selectedDealId) &&
        !_.isNil(selectedUserId) && (
          <FileUpload
            handleFilesUpload={handleFilesUpload}
            onStepBack={handleFilesUploadBack}
            selectedDealId={selectedDealId}
            selectedUserId={selectedUserId}
          />
        )}

      {currentStep === EChecklistFileImportStep.DETAILS &&
        !_.isNil(checklistFileImportId) &&
        !_.isNil(selectedDealId) &&
        !_.isNil(selectedUserId) && (
          <Details
            checklistFileImportId={checklistFileImportId}
            handleGuessedConnectionsSubmit={handleGuessedConnectionsSubmit}
            dealId={selectedDealId}
            userId={selectedUserId}
            showLoading={showLoading}
            enableGuessedConnectionFetching={enableGuessedConnectionFetching}
            guessedConnectionFetchingSetter={guessedConnectionFetchingSetter}
          />
        )}
      {currentStep === EChecklistFileImportStep.IMPORT_COMPLETED && (
        <ImportCompleted handleStartProcessOver={handleStartProcessOver} />
      )}
    </Stack>
  );
};

export default SuperuserChecklistFileImport;
